/* eslint-disable max-lines */
import { composeTheme } from '@css-modules-theme/core'
import classNames from 'classnames'
import { useParams, useRouter, useSearchParams } from 'next/navigation'
import moment from 'moment-timezone'
import { FunctionComponent, useCallback, useContext, useEffect, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Route } from 'next'
import {
  createActiveTicketsList,
  createDateString,
  getButtonText,
  getCategoryAndTimeslotData,
  getNotEndedTimeslotIds,
  isIncomingDateAvailable,
  isValidCalendarDate,
} from '@/utils/components/EventTickets.utils'
import actionContainerStyles from '@/styles/templates/ActionContainer.module.css'
import sharedEventStyles from '@/styles/templates/Events.module.css'
import sharedTicketStyles from '@/styles/templates/Tickets.module.css'
import localStyles from './EventTickets.module.css'
import EventDayTiles from './EventDayTiles'
import DayTile from './DayTile'
import { useSeller } from '@/app/_providers/SellerProvider/SellerProvider'
import { useOurStore } from '@/app/_providers/StoreProvider'
import LoginContext from '@/app/_providers/LoginProvider/LoginContext'
import ModalContext from '@/app/_providers/ModalProvider/ModalContext'
import { CartResetType, useReset } from '@/hooks/useReset'
import ThemeContext from '@/app/_providers/ThemeProvider/ThemeContext'
import useTicketStyles from '@/hooks/useTicketStyles'
import {
  buildCartModificationPayload,
  cartExists,
  getLineItemsByCategory,
  getPromoCodeList,
  getQtyForLineItemsWithMeta,
  getTotals,
  removingAllItems,
} from '@/utils/cart.utils'
import useAdditionalInfo from '@/hooks/useAdditionalInfo'
import { addToCartBlocked, getQuantitiesForTier } from '@/utils/components/tickets.utils'
import {
  EVENTS_CHECKOUT_BASE_PATH,
  EVENTS_SLUG_POSTFIX,
  EXHIBITION_SLUG_POSTFIX,
  PRIMARY_ADMISSION_EVENT_SLUG,
  STEP_SLUG,
} from '@/config/navigation.config'
import TXT from '@/messages'
import useCartActions from '@/hooks/useCartActions'
import { checkIsRepeatingEvent, EventMeta, getEventMeta } from '@/utils/events.utils'
import { SegmentProductViewed } from '@/types/tracking.types'
import { segment } from '@/utils/tracking.utils'
import { TIME_FORMATS } from '@/config/dates.config'
import LoadingSpinner from '@/app/_components/LoadingSpinner/LoadingSpinner'
import ActionContainer, { DISPLAY_VARIANT } from '../ActionContainer/ActionContainer'
import Modal from '@/app/_components/Modal/Modal'
import ActionButton from '@/app/_components/ActionButton/ActionButton'
import { TEST_IDS } from '@/config/html-attributes.config'
import { useUpdateButtonState } from '@/hooks/useUpdateButtonState'
import { Button } from '@/app/_components/Button'
import useEditingState from '@/hooks/useEditingState'
import { getTicketsForNonRecurringEventAction } from '@/app/_actions/tickets.actions'
import OrderItemsSection from '@/app/_components/OrderItemSection/OrderItemSection'
import { QUERY_KEYS } from '@/config/tanstack-querykeys.config'
import { useCart } from '@/app/_providers/CartProvider/CartProvider'
import { convertDevToMWTesting } from '@/utils/temporary.utils'
import TestState from '@/app/_components/TestState/TestState'
import LifetimeIndicator from '@/app/_components/EventTickets/LifetimeIndicator'

type Props = {
  event: Transformed.EventWithTimeslotsAndMeta
}

const EventTickets: FunctionComponent<Props> = (props: Props) => {
  const { timezone } = useSeller()
  const { event } = props
  const { sellerSlug } = useParams<{ sellerSlug: string, slug: string }>()
  const localLineItems = useOurStore((state) => state.localLineItems)
  const lineItemsToDelete = useOurStore((state) => state.lineItemsToDelete)
  const updatePartial = useOurStore((state) => state.updatePartial)
  const resetByGroups = useOurStore((state) => state.resetByGroups)

  const { doReset } = useReset()

  const searchParams = useSearchParams()
  const incomingDate = searchParams.get('date')

  const isDirty = useMemo(() => (
    localLineItems.length > 0 || lineItemsToDelete.length > 0
  ), [localLineItems, lineItemsToDelete])

  // useContext
  const { membershipDetails } = useContext(LoginContext)
  const {
    modalState,
    updateModalState,
  } = useContext(ModalContext)
  const { mobile } = useContext(ThemeContext)
  const themeStyles = useTicketStyles('events')

  // custom hooks
  const router = useRouter()

  const styles = composeTheme([
    { theme: localStyles },
    { theme: sharedTicketStyles },
    { theme: sharedEventStyles },
    { theme: themeStyles },
  ])

  const {
    timeslots,
    sellerId,
    id: eventId,
    meta: eventMeta,
  } = event

  // only request sessions that have not ended; one failed session request fails them all
  const timeslotIds = getNotEndedTimeslotIds(timeslots, timezone)

  const { cart } = useCart()

  const {
    data: ticketsAndAddonsData,
    isFetching: ticketsAndAddonsLoading,
    error: ticketsAndAddonsError,
    refetch: refetchTicketsAndAddons,
  } = useQuery({
    queryKey: [QUERY_KEYS.TICKETS_AND_ADDONS, eventId, timeslotIds, sellerId],
    queryFn: () => getTicketsForNonRecurringEventAction({
      eventSlugOrId: eventId,
      sellerId,
      timeslotIds,
    }),
    enabled: timeslotIds.length > 0,
  })

  const isRecurringEvent = useMemo(() => checkIsRepeatingEvent(event), [event])

  // We don't show the footer if no ticketCategories are currently on sale
  const {
    someOnSale,
    categories,
    hasLifetimeTickets,
    numTimeslots,
    allTimeslotsSoldOut,
  } = useMemo(
    () => getCategoryAndTimeslotData(ticketsAndAddonsData),
    [ticketsAndAddonsData],
  )

  const {
    additionalInfoResponses,
    setAdditionalInfoResponses,
  } = useAdditionalInfo({ categories })

  const existingCart = useMemo(() => cartExists(cart), [cart])

  const {
    localLineItemsTotal,
    lineItemsToDeleteTotal,
  } = useMemo(() => getTotals({
    localLineItems,
    cartLineItems: cart.lineItems,
    lineItemsToDelete,
  }), [localLineItems, cart.lineItems, lineItemsToDelete])

  // Total quantity for tier
  const { adjustedQtyForTier } = useMemo(() => getQuantitiesForTier({
    localLineItems,
    cartLineItems: cart.lineItems,
    lineItemsToDelete,
  }), [localLineItems, cart.lineItems, lineItemsToDelete])

  const { events: cartEventsWithMeta } = useMemo(
    () => (
      getLineItemsByCategory({ lineItems: cart.lineItems })),
    [cart.lineItems],
  )

  const isEditing = useMemo(() => (
    cartEventsWithMeta.some((cartEvent) => (
      cartEvent.event.id === eventId
    ))), [cartEventsWithMeta, eventId])

  const cartLineItemsTotal = useMemo(() => getQtyForLineItemsWithMeta(
    cartEventsWithMeta,
  ), [cartEventsWithMeta])

  const { setCartUpdating, cartUpdating } = useEditingState({})

  const handleUnload = useCallback((evt: BeforeUnloadEvent): void => {
    if (!isDirty) return
    evt.preventDefault()
    evt.returnValue = TXT.alerts.unsavedChanges
  }, [isDirty])

  /** Tested in playwright */
  /* istanbul ignore next */
  const handleBrowseAway = useCallback((url: string): void => {
    if (!isDirty) return
    // parse the current URL and the destination URL to compare pathnames
    const currentPath = window.location.pathname
    const destinationPath = new URL(url, window.location.origin).pathname

    // only show warning if navigating to different pathname (not just query params)
    // clear localLineItems if navigating to the same path
    if (currentPath === destinationPath) {
      // IMPORTANT NOTICE!!!
      // TODO: [FE-2219](https://app.asana.com/1/23646394350278/project/1206566497440920/task/1210630973964198)
      // we are clearing localLineItems here WHICH in most cases is handled by the doReset in the cleanup fn (ln449)
      // but we want this to be handled by doReset which doesn't
      // trigger when a query param changes using useRouter
      resetByGroups(['localItems'])
    }

    if (window.confirm(TXT.alerts.unsavedChanges)) return
    // need to throw an error to stop the route change
    throw 'routeChange aborted.'
  }, [isDirty, resetByGroups])

  const { modifyCart, isModifyPending: addToCartLoading, error: addToCartError, deleteCart } = useCartActions({
    sellerId,
    onSuccess: () => {
      if (!modalState.open.error) {
        updatePartial({ lastStepCompleted: 1 })
        router.push(`/${sellerSlug}/${EVENTS_SLUG_POSTFIX}/${EVENTS_CHECKOUT_BASE_PATH}/${STEP_SLUG.CustomerInfo}` as Route)
      }
    },
    onDeleteAllTickets: () => {
      refetchTicketsAndAddons()
    },
  })

  const isRemovingAllItems = useMemo(
    () => removingAllItems(cartLineItemsTotal, lineItemsToDeleteTotal, localLineItemsTotal),
    [cartLineItemsTotal, lineItemsToDeleteTotal, localLineItemsTotal],
  )

  /**
   * TODO: [TICKT-2173] move this out of this component up to the StepProvider
   * preferably use the function in OrderTickets for this component and OrderAddons
   */
  const onActionBtnClick = useCallback(async (
    firstTimeslotStartTime?: string,
    internalTicketUrlSlug?: string,
  ): Promise<void> => {
    // if the event has an internal ticket url slug, redirect ticket url slug with date params
    if (internalTicketUrlSlug && firstTimeslotStartTime) {
      window.location.href = `/${internalTicketUrlSlug}/${EXHIBITION_SLUG_POSTFIX}/${PRIMARY_ADMISSION_EVENT_SLUG}/${createDateString(
        firstTimeslotStartTime,
        timezone,
      )}`
      return
    }
    setCartUpdating(true)

    // remove dirty check
    window.removeEventListener('beforeunload', handleUnload)

    if (isRemovingAllItems && cartExists(cart)) {
      await deleteCart()
      return
    }

    const {
      ticketIdsToRemove,
      itemsToReserve,
      hasModifications,
    } = buildCartModificationPayload({
      cartLineItems: cart?.lineItems,
      lineItemsToDelete,
      localLineItems,
    })

    if (hasModifications) {
      await modifyCart({ ticketIdsToRemove, itemsToReserve })
        // eslint-disable-next-line no-console
        .catch((e) => { console.log(e) })
    } else {
      // Fallback behavior if no specific targeted modifications are needed
      // eslint-disable-next-line no-console
      await modifyCart().catch((e) => { console.log(e) })
    }
  }, [
    setCartUpdating,
    handleUnload,
    isRemovingAllItems,
    cart,
    modifyCart,
    timezone,
    deleteCart,
    lineItemsToDelete,
    localLineItems,
  ])

  const couponCodes = useMemo(() => (
    cart
      ? getPromoCodeList(cart)
        .join(', ')
      : ''), [cart])

  useEffect(() => {
    if (addToCartLoading) {
      setCartUpdating(false)
    }
  }, [addToCartLoading])

  /**
   * TODO:
   * this is not perfect and needs work
   * An alert that looked better would be nice
   */
  useEffect(() => {
    /** Tested in playwright */
    /* istanbul ignore next */
    window.addEventListener('beforeunload', handleUnload)

    return (): void => {
      window.removeEventListener('beforeunload', handleUnload)
    }
  }, [handleBrowseAway, handleUnload, isDirty, router])

  // on unload, clear local cache
  useEffect(() => {
    const {
      category,
      title,
      slug: eventSlug,
    } = event
    const transformedMeta = eventMeta ? getEventMeta(eventMeta as Transformed.MetaData[]) : {} as EventMeta
    const payload: SegmentProductViewed = {
      name: title,
      category: category.toString(),
      // eslint-disable-next-line camelcase
      product_id: eventId,
      sku: eventId,
      brand: '',
      variant: 'internal sale',
      coupon: couponCodes,
      currency: 'USD',
      price: 0,
      quantity: 0,
      position: 0,
      value: 0,
      url: eventSlug,
      // eslint-disable-next-line camelcase
      image_url: transformedMeta.imageProfile || '',
    }
    segment.productViewed(payload)

    return () => {
      doReset({ type: CartResetType.LocalItemsOnly })
    }
  }, [event, eventId, eventMeta, couponCodes])

  /**
   * activeTickets is an array of tickets that are currently active
   * It filters out sessions for membership tickets
   * It gets active tickets
   * And filters out categories with no tiers
   */
  const activeTickets = useMemo(() => (
    createActiveTicketsList(ticketsAndAddonsData, membershipDetails, timezone)
  ), [ticketsAndAddonsData, timezone, membershipDetails])

  /**
   * eventDays is an array of timeslots for the event
   * It filters out sessions that have no tickets available
   * and maps to only the timeslot data.
   */
  const eventDays: Transformed.Timeslot[] = useMemo(() => activeTickets
    // filter out sessions with no tickets
    // map to only the timeslot data
    .map((session) => (
      session.timeslot
    )), [activeTickets])

  /**
   * Is the incoming date a valid date
   * TODO: need an additional check to ensure the incoming date is an option in our list of activeTickets
   * TODO: this additional check could happen somewhere else if that makes more sense. Need to look into it more
   */
  const isValidDate = useMemo(() => (
    isValidCalendarDate(incomingDate, isRecurringEvent, timezone)
  ), [incomingDate, isRecurringEvent, timezone])

  // Processes and filters event tickets based on user membership and selected date.
  // Returns tickets for a specific date if provided and valid, otherwise returns all active tickets.
  // Filters out lifetime pass tickets based on user's membership status.
  const eventTickets: Transformed.EventTicketsAndAddonsWithTimeslots[] = useMemo(() => {
    // helper function to get active tickets (today or future)

    // if incoming date is invalid, return active tickets
    if (!isValidDate) {
      return activeTickets
    }

    // filter tickets by the specific date
    const ticketsForSpecificDate = activeTickets
      .filter((ticket) => {
        const ticketDate = moment(ticket.timeslot.startTime)
          .tz(timezone)
          .format(TIME_FORMATS.calendarDate)
        return ticketDate === incomingDate
      })

    // If no tickets found for the specific date, fallback to active tickets
    if (!ticketsForSpecificDate.length) {
      return activeTickets
    }

    return ticketsForSpecificDate
  }, [activeTickets, incomingDate, timezone, isValidDate])

  const incomingDateAvailable = useMemo(() => (
    isIncomingDateAvailable(incomingDate, eventTickets, timezone)
  ), [incomingDate, eventTickets, timezone])

  const nextButtonText = useMemo(() => getButtonText(
    isEditing,
    isRemovingAllItems,
    lineItemsToDeleteTotal,
    localLineItemsTotal,
    existingCart,
  ), [isEditing, isRemovingAllItems, lineItemsToDeleteTotal, localLineItemsTotal, existingCart])

  const blockedByAdditionalInfo = useMemo(() => (
    addToCartBlocked(localLineItems, additionalInfoResponses)
  ), [
    additionalInfoResponses,
    localLineItems,
  ])

  // localLineItemsTotal will be 0 when a user navigates back from checkout
  const nextButtonDisabled = useMemo(() => (
    (!existingCart && localLineItemsTotal <= 0 && lineItemsToDeleteTotal <= 0)
    || blockedByAdditionalInfo
    || (addToCartLoading && !addToCartError)
  ), [
    existingCart, localLineItemsTotal,
    lineItemsToDeleteTotal, addToCartLoading,
    addToCartError, blockedByAdditionalInfo,
  ])

  useUpdateButtonState({
    newButtonState: {
      disabled: nextButtonDisabled,
      action: async () => onActionBtnClick(),
      text: nextButtonText,
      digitalWalletPayment: undefined,
    },
    updateStateCondition: true,
    // TODO: revisit this, make sure array is correct
    dependencyArray: [nextButtonDisabled, nextButtonText],
  })

  /**
   * error loading data
   */
  if (ticketsAndAddonsError) {
    return <h3 className={styles.NotAvailable}>{TXT.errors.loadTickets}</h3>
  }

  if (!ticketsAndAddonsLoading && !eventTickets?.length) {
    return <h3 className={styles.NotAvailable}>{TXT.alerts.noTicketsAvailable}</h3>
  }

  /**
   * data loading
   */
  if (ticketsAndAddonsLoading) {
    return (
      <div className={styles.TicketLoadingSpacer}>
        <LoadingSpinner />
      </div>
    )
  }

  if (allTimeslotsSoldOut) {
    return (
      <>
        <LifetimeIndicator hasLifetimeTickets={hasLifetimeTickets} />
        <div className={styles.EventSoldOut} data-testid="event-sold-out">
          <Button
            type="button"
            size="lg"
            weight="bold"
            uppercase
            disabled
          >
            {TXT.alerts.eventSoldOut}
          </Button>
        </div>
      </>
    )
  }

  const internalTicketUrlSlug = convertDevToMWTesting(event.internalTicketUrlSlug)

  const {
    monthAndDayWithYear,
    timeWithAMPM,
  } = TIME_FORMATS

  let recurringEventDay: Nullable<string> = null
  // if the event is has an internal ticket url slug, we show the first timeslot date
  // this is only for events that have an internal ticket url slug, otherwise the date is show above the orderItem
  // TODO: [TICKT-2220](https://app.asana.com/1/23646394350278/project/1206566497440920/task/1210630973964201_)
  if (internalTicketUrlSlug && eventTickets.length > 0) {
    const startTime = moment(eventTickets[0].timeslot.startTime)
      .tz(timezone)
    const endTime = moment(eventTickets[0].timeslot.endTime)
      .tz(timezone)
    recurringEventDay = `
    ${startTime.format(`${monthAndDayWithYear} - ${timeWithAMPM}`)} - ${endTime.format(timeWithAMPM)}`
  }

  return (
    <ActionContainer
      className={actionContainerStyles.EventsTransparent}
      variant={DISPLAY_VARIANT.Events}
      footer={
        someOnSale && (!mobile || internalTicketUrlSlug)
        && (!internalTicketUrlSlug || (isRecurringEvent && incomingDateAvailable)) && (
          <ActionButton
            className="events"
            testId={`${TEST_IDS.ORDER_ITEMS}-next`}
            buttonText={internalTicketUrlSlug ? TXT.buttons.buyTickets : nextButtonText}
            disabled={nextButtonDisabled && (!internalTicketUrlSlug || !incomingDate)}
            onClick={() => onActionBtnClick(eventTickets[0]?.timeslot?.startTime, internalTicketUrlSlug)}
          />
        )
      }
    >

      <TestState id="test-state-event-tickets" data={ticketsAndAddonsData} />
      <LifetimeIndicator hasLifetimeTickets={hasLifetimeTickets} />
      <div className={styles.DayGrid} data-testid="day-tiles-grid">
        {isRecurringEvent && (
          <EventDayTiles
            incomingDate={incomingDate}
            updateModalState={updateModalState}
            eventDays={eventDays}
            eventTickets={eventTickets}
            isRecurringEvent={isRecurringEvent}
          />
        )}
        {/* Only show the toggle button if there are more than 4 days */}
        {isRecurringEvent && eventDays.length > 4 && (
          <button
            type="button"
            className={classNames(styles.DayTile, styles.ShowMore)}
            data-testid="show-more-dates-button"
            onClick={() => {
              updateModalState({ open: { eventDetails: true } })
            }}
          >
            {TXT.buttons.showMoreDates}
          </button>
        )}
      </div>
      {isRecurringEvent && !ticketsAndAddonsLoading && !!eventTickets?.length
        && (
          <Modal
            theme="dark"
            visible={modalState.open.eventDetails}
            setVisible={(isVisible) => updateModalState({ open: { eventDetails: isVisible } })}
            customBody
            body={(
              <div className={classNames(styles.DayGrid, styles.InModal)} data-testid="modal-day-tile-grid">
                {eventDays
                  .map((session) => (
                    <DayTile
                      key={session.id}
                      updateModalState={updateModalState}
                      incomingDate={incomingDate}
                      session={session}
                    />
                  ))}
              </div>
            )}
          />
        )}
      {/**
       * Structure overview
       * - timeslots sorted by start time
       * - ticket categories sorted by display order
       * - tiers sorted by display order
       */}
      {recurringEventDay && isValidDate && (
        <div className={styles.TiersWrapper}>
          <div className={styles.Day} data-testid="selected-event-day">{recurringEventDay}</div>
        </div>
      )}
      {!ticketsAndAddonsLoading
        && !!eventTickets?.length
        && !internalTicketUrlSlug
        && eventTickets
          .map((session) => {
            if (isRecurringEvent && !incomingDateAvailable) return null
            const eventStartMoment = moment.tz(session.timeslot.startTime, timezone)
            const day = eventStartMoment.format(`${monthAndDayWithYear} - ${timeWithAMPM}`)

            return (
              <div
                key={session.timeslot.id}
                className={classNames(styles.TiersWrapper, sharedTicketStyles.TiersWrapper)}
              >
                {/* only shows on events with multiple timeslots */}
                <div data-testid="timeslot">
                  {numTimeslots > 1 && <div className={styles.Day} data-testid="selected-event-day">{day}</div>}
                  <OrderItemsSection
                    items={session.tickets}
                    timeslotId={session.timeslot.id}
                    itemType="tickets"
                    session={session}
                    qtyForTier={adjustedQtyForTier}
                    thisEvent={event}
                    thisCartUpdating={cartUpdating}
                    setAddlInfoResponses={setAdditionalInfoResponses}
                    styles={styles}
                  />
                  {session.addons.length > 0 && (
                    <>
                      <OrderItemsSection
                        items={session.addons}
                        timeslotId={session.timeslot.id}
                        itemType="addons"
                        session={session}
                        qtyForTier={adjustedQtyForTier}
                        thisEvent={event}
                        thisCartUpdating={cartUpdating}
                        setAddlInfoResponses={setAdditionalInfoResponses}
                        styles={styles}
                      />
                    </>
                  )}
                </div>
              </div>
            )
          })}
    </ActionContainer>
  )
}

export default EventTickets
